import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { Chip, Group, Text, Box, Flex } from '@landr/maestro';
import { css } from '@landr/styled-system/css';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { FiltersParams } from 'types/params';
import { InstrumentFamily } from 'types/enums';
import { Instruments } from 'types/enums/instruments';
import { Genres } from 'types/enums/genres';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';

const chipClass = css({
    cursor: 'click',
    border: 'high',

    _hover: {
        backgroundColor: 'surface.neutral.high.hover',
        borderColor: 'border.neutral.medium',
        color: 'text.inverted.high',
    },
});

const TrendingGroup = ({
    label,
    modifiers,
    isSmallUi,
}: {
    label: string;
    modifiers: ModifiersPayload[];
    isSmallUi: boolean;
}) => {
    const { getFilterLabel } = useApiFiltersContext();
    const { applySuggestion } = useSearchBarContext();

    return (
        <Group
            direction={isSmallUi ? 'vertical' : 'horizontal'}
            gap="xs"
            alignItems="baseline"
        >
            <Group.Item>
                <Text fontSize="xs" color="text.medium" textWrap="nowrap">
                    {label}
                </Text>
            </Group.Item>
            <Group.Item flexShrink={10}>
                <Flex gap="xs" flexWrap="wrap">
                    {modifiers.map(([filterParams, slug]) => (
                        <Chip
                            key={`${filterParams}-${slug}`}
                            className={chipClass}
                            size="sm"
                            onClick={() =>
                                applySuggestion([filterParams, slug])
                            }
                        >
                            {getFilterLabel(
                                slug ?? '',
                                filterParams,
                            ).toLowerCase()}
                        </Chip>
                    ))}
                </Flex>
            </Group.Item>
        </Group>
    );
};

const InstrumentsTrending: ModifiersPayload[] = [
    [FiltersParams.instruments, InstrumentFamily.Vocals],
    [FiltersParams.instruments, InstrumentFamily.Guitars],
    [FiltersParams.instruments, InstrumentFamily.Synth],
    [FiltersParams.instruments, InstrumentFamily.Keys],
    [FiltersParams.instruments, InstrumentFamily.Drums],
    [FiltersParams.instruments, InstrumentFamily.Percussion],
    [FiltersParams.instruments, Instruments.EightHundredEight],
    [FiltersParams.instruments, InstrumentFamily.FX],
];

const GenresTrending: ModifiersPayload[] = [
    [FiltersParams.genres, Genres.Trap],
    [FiltersParams.genres, Genres.Rnb],
    [FiltersParams.genres, Genres.Reggaeton],
    [FiltersParams.genres, Genres.AfroHouse],
    [FiltersParams.genres, Genres.House],
    [FiltersParams.genres, Genres.Techno],
    [FiltersParams.genres, Genres.Soul],
    [FiltersParams.genres, Genres.BoomBap],
];

const TagsTrending: ModifiersPayload[] = [
    [FiltersParams.tags, 'melody'],
    [FiltersParams.tags, 'songstarters'],
    [FiltersParams.tags, 'fills'],
    [FiltersParams.tags, 'travis-scott'],
    [FiltersParams.tags, 'kanye-west'],
    [FiltersParams.tags, 'daft-punk'],
    [FiltersParams.tags, 'sza'],
    [FiltersParams.tags, 'uk'],
];

const TrendingSmallUi = 600;

const useTrendingSuggestions = () => {
    const boxRef = useRef<HTMLDivElement>(null);
    const [isSmallUi, setIsSmallUi] = useState(false);

    // Observe container width to detect when we use small Ui
    useEffect(() => {
        const containerElement = boxRef.current;

        const resizeObserver = new ResizeObserver(() => {
            const containerRect = containerElement?.getBoundingClientRect();

            if (containerRect) {
                const isSmallUi = containerRect?.width <= TrendingSmallUi;

                setIsSmallUi(isSmallUi);
            }
        });

        containerElement && resizeObserver.observe(containerElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return { boxRef, isSmallUi };
};

export const TrendingSuggestions = () => {
    const { boxRef, isSmallUi } = useTrendingSuggestions();

    return (
        <Box ref={boxRef} px="md" py="sm">
            <Group direction="vertical" gap="xs">
                <Group.Item>
                    <TrendingGroup
                        isSmallUi={isSmallUi}
                        label={t`Top Instruments`}
                        modifiers={InstrumentsTrending}
                    />
                </Group.Item>
                <Group.Item>
                    <TrendingGroup
                        isSmallUi={isSmallUi}
                        label={t`Top Genres`}
                        modifiers={GenresTrending}
                    />
                </Group.Item>
                <Group.Item>
                    <TrendingGroup
                        isSmallUi={isSmallUi}
                        label={t`Top Tags`}
                        modifiers={TagsTrending}
                    />
                </Group.Item>
            </Group>
        </Box>
    );
};
